// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useCallback, useRef } from "react";
import { CASAuthManager } from "../lib/cas-manager";
import { CASConfig, CASUser, AuthState } from "../types";
import { storage } from "../lib/storage";
import {
  getTokenExpirationMinutes,
  shouldRefreshToken,
} from "../lib/token";

/**
 * React hook for CAS authentication
 *
 * @example
 * ```tsx
 * const { user, isLoading, error, logout } = useCASAuth({
 *   casLoginUrl: "https://login.umd.edu/cas/login",
 *   serviceUrl: "http://localtest.dev.umd.edu:8080",
 *   apiGatewayUrl: "https://api.example.com/auth/cas/login",
 *   debug: true
 * });
 * ```
 */
export function useCASAuth(config: CASConfig) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    error: null,
  });

  const [manager] = useState(() => new CASAuthManager(config));

  // Track if we've already initiated a redirect to prevent StrictMode double-invocation
  const redirectInitiatedRef = useRef(false);
  // Same guard for ticket exchange — CAS service tickets are single-use, so a
  // double-mount would consume the ticket on the first call and 500 on the second.
  const ticketExchangeInitiatedRef = useRef(false);

  // Auto-refresh state
  const [showRefreshWarning, setShowRefreshWarning] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState(0);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const checkTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Configuration with defaults
  const enableAutoRefresh = config.enableAutoRefresh !== false; // default true
  const refreshWarningMinutes = config.refreshWarningMinutes || 10; // default 10 minutes
  const refreshWarningDurationSeconds = config.refreshWarningDurationSeconds || 30; // default 30 seconds

  // Clear any stale redirect count from previous failed sessions on mount
  useEffect(() => {
    const initialCount = storage.getRedirectCount();
    if (initialCount > 0) {
      console.log('[CAS Auth] Clearing stale redirect count from previous session:', initialCount);
      storage.resetRedirectCount();
    }
  }, []); // Empty array = run once on mount

  // Dispatch authentication event
  const dispatchAuthEvent = useCallback((authenticated: boolean, user: CASUser | null) => {
    if (typeof document !== 'undefined') {
      // Dispatch auth-state-change event
      document.dispatchEvent(new CustomEvent('auth-state-change', {
        detail: { authenticated, user },
        bubbles: true
      }));

      // Also dispatch access-granted event for compatibility
      if (authenticated && user) {
        document.dispatchEvent(new CustomEvent('access-granted', {
          detail: { authenticated, user },
          bubbles: true
        }));
      }
    }
  }, []);

  // Initialize authentication
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if there's a ticket in the URL
        if (manager.hasTicketInURL()) {
          const ticket = manager.getTicketFromURL();

          if (ticket && !ticketExchangeInitiatedRef.current) {
            ticketExchangeInitiatedRef.current = true;

            // Strip the ticket from the URL synchronously, before awaiting the
            // exchange, so a re-mount can't observe it and fire a second request.
            manager.cleanupURL();

            // Exchange ticket for JWT
            const user = await manager.exchangeTicketForToken(ticket);

            // Update state
            setAuthState({
              user,
              isLoading: false,
              error: null,
            });

            // Dispatch authentication event
            dispatchAuthEvent(true, user);

            return;
          }
        }

        // Check for existing valid session
        if (manager.hasValidSession()) {
          const user = manager.getCurrentUser();
          setAuthState({
            user,
            isLoading: false,
            error: null,
          });

          // Dispatch authentication event
          dispatchAuthEvent(true, user);
          return;
        }

        // No valid session, redirect to login
        // Check if we've already initiated a redirect (protects against StrictMode double-invocation)
        if (!redirectInitiatedRef.current) {
          redirectInitiatedRef.current = true;
          manager.redirectToLogin();
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Authentication failed";

        setAuthState({
          user: null,
          isLoading: false,
          error: errorMessage,
        });

        // Dispatch authentication failure event
        dispatchAuthEvent(false, null);
      }
    };

    initAuth();
  }, [manager, dispatchAuthEvent]);

  // Logout function
  const logout = useCallback(() => {
    manager.logout();
  }, [manager]);

  // Get token function
  const getToken = useCallback(() => {
    return manager.getToken();
  }, [manager]);

  // Add auth header to axios config
  const addAuthHeader = useCallback(
    (axiosConfig: Record<string, unknown>) => {
      return manager.addAuthHeader(axiosConfig);
    },
    [manager]
  );

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      if (checkTimerRef.current) {
        clearInterval(checkTimerRef.current);
      }
    };
  }, []);

  // Handle token refresh
  const handleRefreshNow = useCallback(() => {
    // Clear all timers
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }

    setShowRefreshWarning(false);
    manager.refreshToken();
  }, [manager]);

  // Start periodic token check
  const startTokenCheckTimer = useCallback(() => {
    // Clear existing timer
    if (checkTimerRef.current) {
      clearInterval(checkTimerRef.current);
    }

    const token = manager.getToken();
    if (!token) {
      return;
    }

    // Check every minute
    const checkFn = () => {
      const currentToken = manager.getToken();
      if (!currentToken) {
        return;
      }

      // Skip the first check right after a silent refresh redirect. The page
      // just reloaded with a fresh token, but the immediate-on-mount check can
      // race the newly-stored token and flash the warning once. Consume the
      // flag so subsequent interval checks behave normally.
      if (storage.getItem("cas_silent_refresh")) {
        storage.removeItem("cas_silent_refresh");
        return;
      }

      if (shouldRefreshToken(currentToken, refreshWarningMinutes)) {
        // Stop checking
        if (checkTimerRef.current) {
          clearInterval(checkTimerRef.current);
        }

        // Show warning
        setShowRefreshWarning(true);
        setRefreshCountdown(refreshWarningDurationSeconds);

        // Update countdown every second
        countdownTimerRef.current = setInterval(() => {
          setRefreshCountdown((prev) => {
            if (prev <= 1) {
              // Time's up, refresh now
              if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
              }
              setShowRefreshWarning(false);
              manager.refreshToken();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    };

    checkTimerRef.current = setInterval(checkFn, 60 * 1000);

    // Also check immediately
    checkFn();
  }, [manager, refreshWarningMinutes, refreshWarningDurationSeconds]);

  // Handle cancel refresh
  const handleCancelRefresh = useCallback(() => {
    // Clear timers
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }

    setShowRefreshWarning(false);
    setRefreshCountdown(0);

    // Wait 5 minutes before checking again
    if (checkTimerRef.current) {
      clearInterval(checkTimerRef.current);
    }
    checkTimerRef.current = setTimeout(() => {
      startTokenCheckTimer();
    }, 5 * 60 * 1000) as unknown as NodeJS.Timeout;
  }, [startTokenCheckTimer]);


  // Start auto-refresh monitoring when user is authenticated
  useEffect(() => {
    if (authState.user && !authState.isLoading && enableAutoRefresh) {
      startTokenCheckTimer();
    }

    return () => {
      if (checkTimerRef.current) {
        clearInterval(checkTimerRef.current);
      }
    };
  }, [authState.user, authState.isLoading, enableAutoRefresh, startTokenCheckTimer]);

  return {
    user: authState.user,
    isLoading: authState.isLoading,
    error: authState.error,
    logout,
    getToken,
    addAuthHeader,
    // Refresh-related
    showRefreshWarning,
    refreshCountdown,
    minutesUntilExpiry: authState.user?.token
      ? getTokenExpirationMinutes(authState.user.token) || 0
      : 0,
    onRefreshNow: handleRefreshNow,
    onCancelRefresh: handleCancelRefresh,
  };
}